import React from "react";
import styles from "./Overall.module.css";

import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import { stats as statsApi, errorMessage } from "shared/api";

import StatsTotalCard from "../components/StatsTotalCard";
import CustomerActivityGraph from "../components/CustomerActivityGraph";
import StatsLineGraph from "../components/StatsLineGraph";
import StatsPieChart from "../components/StatsPieChart";

export function Overall() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  // default to Clothing, like the design
  const [activeCategory, setActiveCategory] = React.useState("Clothing");
  const reqIdRef = React.useRef(0);

  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    statsApi
      .overall()
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(errorMessage(err, "Couldn't load the statistics."));
        setLoading(false);
      });
  }, []);

  if (error) {
    return (
      <div className={styles.overall}>
        <AlertNotice title="Couldn't load statistics">{error}</AlertNotice>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className={styles.overall}>
        <Loader size={60} />
      </div>
    );
  }

  const {
    totalCards = [],
    customerActivity = {},
    lineGraphLines,
    overallPie,
    categoryTabs = [],
    categoryDataByKey = {},
  } = data;

  const activeCategoryData =
    categoryDataByKey[activeCategory] ||
    categoryDataByKey[categoryTabs[0]] ||
    {};

  const {
    lineGraphData = [],
    categoryPieData = [],
    lines: categoryLines,
  } = activeCategoryData;
  const lines = categoryLines || lineGraphLines;

  return (
    <div className={styles.overall}>
      {/* ========== GENERAL SECTION ========== */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>General</h2>

        <div className={styles.totalsRow}>
          {totalCards.map((card) => (
            <StatsTotalCard
              key={card.id}
              title={card.title}
              total={card.total}
              sixWeekAvg={card.sixWeekAvg}
            />
          ))}
        </div>

        <div className={styles.generalRow}>
          <CustomerActivityGraph
            data={customerActivity.data}
            activeDay={customerActivity.activeDay}
            title="Customer Activity"
          />

          <StatsPieChart data={overallPie} />
        </div>
      </section>

      {/* ========== STATS PER CATEGORY SECTION ========== */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Stats By Category</h2>

        <div className={styles.categoryTabs}>
          {categoryTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={
                tab === activeCategory
                  ? `${styles.tab} ${styles.tabActive}`
                  : styles.tab
              }
              onClick={() => setActiveCategory(tab)}
              title={`Show statistics for ${tab}.`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className={styles.statsRow}>
          <StatsLineGraph
            data={lineGraphData}
            lines={lines}
            xKey="day"
            rangeLabel="All time, by weekday"
            yTickFormatter={(v) => `$${v}`}
          />

          <div className={styles.categoryPies}>
            {categoryPieData.map((pie) => (
              <StatsPieChart key={pie.id} data={pie.data} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
